import Router, { RawLocation, Route, RouteConfig } from 'vue-router';

export async function navigateTo(
	router: Router,
	location: RawLocation,
	method: 'push' | 'replace' = 'push',
): Promise<void> {
	// Matching the URL drops params that belong to the previous screen.
	const { fullPath } = router.resolve(location).route;
	try {
		await router[method](fullPath);
	} catch (error) {
		if (!Router.isNavigationFailure(error)) throw error;
	}
}

export function replaceTab(router: Router, name: string): Promise<void> {
	const { params, query, hash } = router.currentRoute;
	return navigateTo(router, { name, params, query, hash }, 'replace');
}

export function isSamePage(router: Router, to: Route, from: Route): boolean {
	const page = to.matched[0];
	if (!page?.name || page !== from.matched[0]) return false;
	return (
		router.resolve({ name: page.name, params: to.params }).route.path ===
		router.resolve({ name: page.name, params: from.params }).route.path
	);
}

export function getEntityPage(
	route: Route,
	entities: readonly { id?: number | string }[],
	prefix: string,
	param: string,
): number {
	if (!route.name?.startsWith(`${prefix}.detail.`)) return 1;
	const index = entities.findIndex(entity => String(entity.id) === String(route.params[param]));
	return index < 0 ? 1 : index + 2;
}

// Tabs render in their host page. Partial paths redirect to their first listed tab.
export function tabRoutes(
	prefix: string,
	paths: readonly string[],
	meta: RouteConfig['meta'] = { auth: true },
): RouteConfig[] {
	const routes: RouteConfig[] = paths.map(path => ({
		path,
		name: `${prefix}.${path.replace(/\//g, '.')}`,
		meta,
	}));
	const knownPaths = new Set(paths);
	for (const path of paths) {
		const segments = path.split('/');
		segments.pop();
		while (segments.length) {
			const parent = segments.join('/');
			if (!knownPaths.has(parent)) {
				routes.push({
					path: parent,
					name: `${prefix}.${parent.replace(/\//g, '.')}`,
					redirect: { name: `${prefix}.${path.replace(/\//g, '.')}` },
					meta,
				});
				knownPaths.add(parent);
			}
			segments.pop();
		}
	}
	return routes;
}
